import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, ChevronRight } from "lucide-react";
import { LOGO_IMAGE_WIDTH, sizedUploadUrl } from "@/lib/imageUrl";

type Metric = {
  label: string;
  value: string;
  tone?: "primary" | "green" | "blue" | "orange" | "neutral";
};

export function CryptoExchangeCard(props: {
  href: string;
  name: string;
  tagline?: string | null;
  logoUrl?: string | null;
  rating?: number | null;
  badge?: string | null;
  featured?: boolean;
  metrics?: Metric[];
  ctaLabel?: string;
}) {
  const rating = typeof props.rating === "number" && Number.isFinite(props.rating) ? Math.max(0, Math.min(5, props.rating)) : 0;
  const metrics = Array.isArray(props.metrics) ? props.metrics.slice(0, 3) : [];
  const ctaLabel = String(props.ctaLabel || "View Exchange");

  const renderRatingStars = (sizeClass: string) => {
    // role="img" is required for the aria-label — see BrokerCard.
    return (
      <div
        role="img"
        className="flex items-center gap-1"
        aria-label={`Rating ${rating.toFixed(1)} out of 5`}
      >
        {[...Array(5)].map((_, i) => {
          const fill = Math.max(0, Math.min(1, rating - i));
          const pct = `${Math.round(fill * 100)}%`;
          return (
            <span key={i} className={"relative inline-block " + sizeClass}>
              <Star className={"absolute inset-0 " + sizeClass + " text-white/10 fill-white/10"} />
              <span className="absolute inset-0 overflow-hidden" style={{ width: pct }}>
                <Star className={sizeClass + " text-primary fill-primary"} />
              </span>
            </span>
          );
        })}
      </div>
    );
  };

  const toneClass = (tone: Metric["tone"]) => {
    if (tone === "green") return "text-emerald-300";
    if (tone === "blue") return "text-sky-300";
    if (tone === "orange") return "text-orange-300";
    if (tone === "primary") return "text-primary";
    return "text-gray-100";
  };

  return (
    <Link href={props.href}>
      <div className="group relative h-full">
        <div
          className={
            "absolute -inset-0.5 rounded-2xl bg-gradient-to-br via-white/5 to-transparent blur-sm transition-opacity " +
            (props.featured
              ? "from-primary/50 opacity-55 group-hover:opacity-80"
              : "from-primary/25 opacity-35 group-hover:opacity-60")
          }
        />
        <Card className="relative h-full rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#121a2c] to-[#0b1222] shadow-[0_18px_50px_rgba(0,0,0,0.45)]">
          <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_20%_0%,rgba(234,179,8,0.18)_0%,rgba(0,0,0,0)_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_90%_20%,rgba(56,189,248,0.10)_0%,rgba(0,0,0,0)_55%)]" />
          {/*
            Self-hosted. This used to point at www.transparenttextures.com, i.e.
            a whole extra origin (DNS + TCP + TLS) that the browser had to open
            during first paint just to fetch a 144-byte tile. PageSpeed listed it
            as a preconnect candidate worth ~310 ms of LCP and it preconnected to
            nothing. The bytes are the same file, now served from /img/ on the
            already-open connection and cached for a year.
          */}
          <div className="absolute inset-0 opacity-[0.08] bg-[url('/img/carbon-fibre.png')]" />

          <CardContent className="relative z-10 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div className="relative w-12 h-12 rounded-xl bg-[#070b14] border border-white/10 shadow-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                  {props.logoUrl ? (
                    <img
                      src={sizedUploadUrl(props.logoUrl, LOGO_IMAGE_WIDTH)}
                      alt={props.name}
                      className="w-full h-full object-contain p-1.5"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-white font-black text-base">{String(props.name || "").slice(0, 2).toUpperCase()}</div>
                  )}
                  <div className="absolute inset-0 ring-1 ring-white/10 rounded-xl" />
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <h3 className="min-w-0 font-black text-[18px] text-white leading-tight truncate">{props.name}</h3>
                  </div>
                  <p className="mt-1 text-xs text-gray-400 line-clamp-2">
                    {String(props.tagline || "Trusted crypto exchange").trim()}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {renderRatingStars("w-4 h-4")}
                <span className="text-white font-black">{rating ? rating.toFixed(1) : "—"}</span>
              </div>
            </div>

            {metrics.length ? (
              <div className="mt-4 rounded-xl border border-white/10 bg-[#070b14]/60 overflow-hidden">
                <div className="grid grid-cols-2">
                  {metrics.slice(0, 2).map((m, idx) => (
                    <div
                      key={m.label}
                      className={
                        "px-3 py-3 sm:px-4 sm:py-3.5 text-center " +
                        (idx === 0 ? "border-r border-white/10" : "")
                      }
                    >
                      <div className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                        {m.label}
                      </div>
                      <div className={"mt-1 text-[15px] font-black tabular-nums " + toneClass(m.tone)}>
                        {m.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <Button className="mt-5 w-full h-12 rounded-xl bg-white/5 hover:bg-primary border border-white/10 hover:border-primary text-white hover:text-black font-black uppercase tracking-widest text-[10px] transition-all">
              {ctaLabel}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </Link>
  );
}
